import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Box
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { addRoutes } from './action.ts';
import { toast } from 'react-toastify';

interface AddRouteModalProps {
  open: boolean;
  institutionId: string; 
  onClose: () => void;
  onSuccess: () => void;
}

const AddRouteModal: React.FC<AddRouteModalProps> = ({
  open,
  institutionId,
  onClose,
  onSuccess
}) => {
  const [routeId, setRouteId] = useState('');
  const [routeName, setRouteName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Regex to match only alphabets, numerals, hyphens, and underscores
  const routeIdRegex = /^[a-zA-Z0-9-_]+$/;
  
  // Determine if the current input is invalid (only show error if there's text)
  const isRouteIdInvalid = routeId.length > 0 && !routeIdRegex.test(routeId);

  const handleClose = () => {
    setRouteId('');
    setRouteName('');
    onClose();
  };

  const handleSubmit = () => {
    if (!routeId.trim()) {
      toast.error("Route ID is required");
      return;
    }
    
    // Block submission if regex fails
    if (!routeIdRegex.test(routeId)) {
      toast.error("Route ID can only contain alphabets, numerals, hyphens, and underscores");
      return;
    }

    if (!routeName.trim()) {
      toast.error("Route Name is required");
      return;
    }
    if (!institutionId) {
      toast.error("Institution is missing");
      return;
    }

    setIsSubmitting(true);
    addRoutes(
      institutionId,
      routeName,
      routeId, // maps to route_code in the API
      (response: any) => {
        setIsSubmitting(false);
        toast.success("Route added successfully");
        onSuccess();
        handleClose();
      },
      (errorMsg: any) => {
        setIsSubmitting(false);
        toast.error(errorMsg?.error || errorMsg?.message || "Failed to add route");
      }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <Box
        sx={{
          marginLeft: {
            xs: '0px',
            sm: '-180px',
          },
        }}
      >        
        <Heading title="Add Route" closeIcon="true" onClose={handleClose} />
      </Box>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          autoFocus
          label="Route ID / Code"
          placeholder="Enter route ID"
          type="text"
          fullWidth
          variant="outlined"
          size="small"
          value={routeId}
          onChange={(e) => setRouteId(e.target.value)}
          error={isRouteIdInvalid}
          helperText={isRouteIdInvalid ? "Only alphabets, numbers, '-', and '_' are allowed" : ""}
        />
        <TextField
          label="Route Name"
          placeholder="Enter route name"
          type="text"
          fullWidth
          variant="outlined"
          size="small"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          className="custom-cancel-button" 
          sx={{ textTransform: 'none' }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          className="custom-button"
          disabled={isSubmitting || isRouteIdInvalid}
        >
          Add Route
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRouteModal;